/**
 * The single live socket for the app. All store writes from server frames happen here, so the UI
 * stays declarative and there is exactly one connection per device (docs/CONTRACT.md §5).
 */
import { ChatSocket } from '@olmt/shared'
import { api } from './api'
import { refreshAccount } from './account'
import { callEndEffect, shouldDismissRing } from './callRules'
import { chatMuted, messageBlip } from './soundRules'
import { blip } from './sounds'
import { useAuthStore } from '../stores/authStore'
import { useCallStore } from '../stores/callStore'
import { useChatStore } from '../stores/chatStore'
import { useUiStore } from '../stores/uiStore'

let socket: ChatSocket | null = null

/** Typing frames are advisory; throttle to one per chat per second. */
const lastTypingSentAt: Record<string, number> = {}

/**
 * Chat ids with a `GET /api/chats/:id` in flight. The frozen protocol has no `chat.created` event
 * (docs/CONTRACT.md §5), so a chat that appeared after `ready` is only announced by a frame that
 * references it; one lookup per chat id keeps a burst of frames from becoming a fetch storm.
 */
const pendingChatFetches: Record<string, Promise<void> | undefined> = {}

/**
 * Effects of frames that referenced a chat the client did not know yet, replayed in arrival order
 * once the lookup lands. They are deferred rather than applied early so no frame is counted twice
 * against the snapshot the server returns.
 */
const pendingChatEffects: Record<string, Array<() => void>> = {}

/**
 * Materialises a chat the client does not know yet, then runs `apply` against it. `apply` receives
 * whether the chat was already known: a deferred frame lands after the lookup, whose `ChatView`
 * already reflects everything persisted before it (see the unread rule in the `message` handler).
 * Both tables are keyed by chat id and empty themselves as lookups settle.
 */
function ensureChat(chatId: string, apply?: (known: boolean) => void): void {
  if (useChatStore.getState().chats[chatId]) {
    apply?.(true)
    return
  }
  if (apply) {
    const queued = pendingChatEffects[chatId] ?? []
    queued.push(() => apply(false))
    pendingChatEffects[chatId] = queued
  }
  if (pendingChatFetches[chatId]) return

  // A lookup belongs to the session that started it; a token change (sign out) drops its result.
  const token = useAuthStore.getState().token
  const request = api
    .chat(chatId)
    .then(({ chat }) => {
      const queued = pendingChatEffects[chatId] ?? []
      delete pendingChatEffects[chatId]
      if (useAuthStore.getState().token !== token) return
      // Another path (bootstrap, `openChat`, a chat created here) may have materialised the chat
      // while the lookup was in flight; its entry is no older than this snapshot.
      if (!useChatStore.getState().chats[chatId]) useChatStore.getState().upsertChat(chat)
      for (const effect of queued) effect()
    })
    .catch(() => {
      // Not a member, deleted or offline: drop the queued effects. The next frame retries.
      delete pendingChatEffects[chatId]
    })
    .finally(() => {
      delete pendingChatFetches[chatId]
    })
  pendingChatFetches[chatId] = request
}

export function getSocket(): ChatSocket | null {
  return socket
}

export function connectSocket(): ChatSocket | null {
  const token = useAuthStore.getState().token
  if (!token) return null
  if (socket) return socket

  const instance = new ChatSocket({
    url: api.wsUrl(),
    token,
    // §11: the api closes the live socket when the account stops being `approved`. Re-read the
    // account so the root layout swaps the shell for the waiting screen; 4403 is terminal in
    // `ChatSocket`, and the gate's effect opens a new socket once an admin approves again.
    onNotApproved: () => {
      void refreshAccount()
    },
  })
  const chatState = useChatStore.getState()

  instance.onStateChange((state) => {
    useUiStore.getState().setSocketState(state)
  })

  instance.on('ready', (frame) => {
    const online = new Set(frame.online)
    for (const userId of frame.online) chatState.setPresence(userId, { online: true, lastSeenAt: null })
    for (const [userId, presence] of Object.entries(useChatStore.getState().presence)) {
      if (!online.has(userId) && presence.online) {
        chatState.setPresence(userId, { online: false, lastSeenAt: presence.lastSeenAt })
      }
    }
  })

  instance.on('message', (frame) => {
    ensureChat(frame.chatId, (known) => {
      const store = useChatStore.getState()
      const isMine = frame.message.authorId === useAuthStore.getState().user?.id
      // Read before `upsertMessage` rewrites `lastMessage` (see the deferred rule below).
      const knownLastId = store.chats[frame.chatId]?.lastMessage?.id ?? null
      store.upsertMessage(frame.message)
      // The app's own blip for an arrival worth announcing: not our own echo, not a muted chat,
      // not the thread on screen, and only while the tab is visible (the rules are `soundRules.ts`,
      // shared word-for-word with the native client).
      const chat = useChatStore.getState().chats[frame.chatId]
      if (
        messageBlip({
          mine: isMine,
          muted: chatMuted(chat?.mutedUntil ?? null),
          openChatId: useUiStore.getState().activeChatId,
          chatId: frame.chatId,
          visible: document.visibilityState === 'visible',
        })
      ) {
        blip('message')
      }
      if (isMine) return
      if (useUiStore.getState().activeChatId === frame.chatId) {
        store.clearUnread(frame.chatId, frame.message.id)
        instance.send({ t: 'read', chatId: frame.chatId, messageId: frame.message.id })
        return
      }
      // A deferred frame lands after the lookup, which already counts every message persisted
      // before it in `unreadCount`. Ids are UUIDv7, so `lastMessage.id >= message.id` means the
      // snapshot already includes this message and bumping again would double-count it.
      if (!known && knownLastId !== null && knownLastId >= frame.message.id) return
      store.bumpUnread(frame.chatId, frame.message.createdAt)
    })
  })

  // Both frames touch a message this client may not hold (a page it never loaded, or a chat it
  // just learned about): `applyEdited`/`applyDeleted` replace or mark in place and ignore the rest,
  // never append — the `GET /api/chats/:id/messages` fallback is what carries the row to a client
  // that missed the frame.
  instance.on('message.edited', (frame) => {
    ensureChat(frame.chatId, () => useChatStore.getState().applyEdited(frame.message))
  })

  instance.on('message.deleted', (frame) => {
    ensureChat(frame.chatId, () => useChatStore.getState().applyDeleted(frame.chatId, frame.messageId))
  })

  // W9: the reactions on one message changed. The frame carries the whole aggregated list, so the
  // stored row's `reactions` is *replaced* — merging deltas would need ordering rules of its own.
  // A message this client does not hold is left alone, the same rule as the two frames above.
  instance.on('message.reaction', (frame) => {
    ensureChat(frame.chatId, () =>
      useChatStore.getState().applyReactions(frame.chatId, frame.messageId, frame.reactions),
    )
  })

  // W11: the tallies on one poll changed. The frame carries the whole view — options and voters —
  // so the stored row's `poll` is *replaced*, never merged, the same rule as the reaction list. A
  // message this client does not hold is left alone: it belongs to a page that was never loaded,
  // and the `GET .../messages` fallback is what carries the row when that page arrives.
  instance.on('message.poll', (frame) => {
    ensureChat(frame.chatId, () =>
      useChatStore.getState().applyPoll(frame.chatId, frame.messageId, frame.poll),
    )
  })

  // W13: the chat's timer changed. `ChatView.disappearingSeconds` is what the details row, the
  // options' pressed state and the thread's line all read, so one patch moves every one of them —
  // and the chat may be one this client has never listed, hence the same materialisation as the pin.
  instance.on('chat.disappearing', (frame) => {
    ensureChat(frame.chatId, () =>
      useChatStore.getState().patchChat(frame.chatId, { disappearingSeconds: frame.seconds }),
    )
  })

  // W13: messages past the chat's timer are gone. The rows are *dropped* from the store — no
  // tombstone — and the chat row's preview and bar move off them (see `applyExpired`). There is
  // nothing to materialise a chat for here: a chat this client does not hold has no rows to drop.
  instance.on('messages.expired', (frame) => {
    useChatStore.getState().applyExpired(frame.chatId, frame.messageIds)
  })

  instance.on('typing', (frame) => {
    // `publishToChat` reaches every member's sockets including the sender (docs/CONTRACT.md §5,
    // apps/api/src/ws.ts `broadcastTyping`), so the sender would otherwise see their own indicator
    // in both the thread pane and the chat list.
    if (frame.userId === useAuthStore.getState().user?.id) return
    ensureChat(frame.chatId, () => {
      useChatStore.getState().setTyping(frame.chatId, frame.userId, frame.until)
      // Expire the indicator locally so the UI does not need a ticking clock. `setTyping` with a
      // past deadline prunes stale entries and never adds one.
      const delay = frame.until - Date.now()
      if (delay > 0) {
        setTimeout(() => useChatStore.getState().setTyping(frame.chatId, frame.userId, 0), delay + 50)
      }
    })
  })

  // A `read` frame advances one member's cursor. The viewer's own cursor is `clearUnread`'s, and
  // the peer's is what draws the second check; both also seed from `ChatView.reads` at load time,
  // because these frames are live-only (A15).
  instance.on('read', (frame) => {
    ensureChat(frame.chatId, () => {
      if (frame.userId === useAuthStore.getState().user?.id) return
      useChatStore.getState().advancePeerRead(frame.chatId, frame.userId, frame.messageId)
    })
  })

  instance.on('presence', (frame) => {
    useChatStore.getState().setPresence(frame.userId, {
      online: frame.online,
      lastSeenAt: frame.lastSeenAt,
    })
  })

  // W5: the chat's one pinned message changed — the bar goes up or down for *every* member, and
  // `message: null` is the bar coming down (an unpin, or the api clearing the pin when the pinned
  // message was deleted, which it broadcasts in the same breath). The chat may be one this client
  // has never listed (a frame for a chat it was added to), so it is materialised like any other
  // frame's chat. The message itself is *not* pushed into the thread's list: it belongs to a page
  // the client may not hold, and appending it would tear the history's order.
  instance.on('chat.pinned', (frame) => {
    ensureChat(frame.chatId, () =>
      useChatStore.getState().patchChat(frame.chatId, { pinnedMessage: frame.message }),
    )
  })

  instance.on('call.ring', (frame) => {
    // The ring must not wait on a lookup; unknown chats are materialised in the background.
    ensureChat(frame.chatId)
    // Record that this chat has a live call, so the "Video call" button JOINS it instead of
    // starting a second one. Without this the flag is only ever read from the chat list fetched
    // at load time, so a client whose data predates the call starts a duplicate room — measured:
    // one chat, two rooms, the caller alone in the second.
    useChatStore.getState().patchChat(frame.chatId, { hasActiveCall: true, activeCall: frame.call })
    // A ring is for the callee, and this is the caller's own frame. The api broadcasts `call.ring`
    // to the whole chat topic — the caller included — while the push alongside it goes to "every
    // other chat member" (routes/calls.ts), so the caller's devices used to ring for their own
    // call: measured, a raw `POST /api/chats/:id/calls` put the caller's own ring dialog on screen
    // ~2.5 s later. `hasActiveCall` above already carries everything the caller needs from the
    // frame.
    const calls = useCallStore.getState()
    if (frame.call.startedBy === useAuthStore.getState().user?.id) return
    // A ring that targets members of a call this client is already in — `POST /api/calls/:id/ring`
    // rings *additional* members into a live call — must not raise the incoming screen for the room
    // the user is sitting in. It would arm a second join answer for a call they are on; the
    // `hasActiveCall` patch above already carried everything else the frame says.
    if (calls.active?.call.id === frame.call.id) return
    calls.setIncoming({ call: frame.call, caller: frame.caller, fromPush: false })
  })

  instance.on('call.end', (frame) => {
    const meId = useAuthStore.getState().user?.id ?? null
    const calls = useCallStore.getState()
    const chat = useChatStore.getState().chats[frame.chatId]
    // `by` and `reason` decide this, not the participant count (A15): a member declining must not
    // tear down the call everyone else is still in.
    const effect = callEndEffect({
      by: frame.by,
      reason: frame.reason,
      meId,
      inCall: calls.active?.call.id === frame.callId,
      ringing: shouldDismissRing(calls.incoming, frame.callId),
      otherMemberIds: (chat?.members ?? [])
        .filter((member) => member.id !== meId)
        .map((member) => member.id),
    })

    // The next tap on "Video call" should start a fresh call once the row is closed; a decline
    // leaves it live, so the chip stays up and the button keeps joining.
    if (effect.closeChatCall) {
      useChatStore.getState().patchChat(frame.chatId, { hasActiveCall: false, activeCall: null })
    }
    if (effect.dismissRing) calls.setIncoming(null)
    if (effect.leaveCall) calls.clearActive()

    if (effect.declinedBy !== null) {
      const name = chat?.members.find((member) => member.id === effect.declinedBy)?.displayName ?? 'They'
      calls.setNotice(`${name} declined the call`)
      // The decliner was the last member who could answer, so the room is empty: close the call the
      // caller opened instead of leaving a "live" chip pointing at nobody.
      void api.endCall(frame.callId).catch(() => undefined)
    }
  })

  // W7: the sibling ring's answer. One of this user's *other* devices answered or declined, and the
  // frame is sent to their own sockets only — so the device that acted receives it too, with its ring
  // already down; the rule makes that a no-op. The store clears `incoming`, which is the same clear an
  // answer or a decline here performs (the tone and the ring surface are driven by it), and the native
  // ring notification follows it exactly as it does for those.
  instance.on('call.dismiss', (frame) => {
    if (useCallStore.getState().dismissRing({ callId: frame.callId, reason: frame.reason })) {
      useCallStore.getState().clearActive()
    }
  })

  socket = instance
  instance.connect()
  return instance
}

export function disconnectSocket(): void {
  socket?.disconnect()
  socket = null
}

/**
 * Re-open the socket unless it is already up.
 *
 * Needed because `ChatSocket` deliberately does not reconnect after a close with code 1000 — that
 * code is the api's "another connection owns this device now" (CONTRACT §5, `registerSocket`), and
 * the native background link produces exactly that when the app returns to the foreground. The
 * ring bridge calls this once the native socket has been told to stand down.
 */
export function ensureSocketConnected(): void {
  if (!socket) {
    connectSocket()
    return
  }
  if (socket.state === 'open' || socket.state === 'connecting') return
  socket.connect()
}

export function sendTyping(chatId: string): void {
  const now = Date.now()
  if ((lastTypingSentAt[chatId] ?? 0) + 1000 > now) return
  lastTypingSentAt[chatId] = now
  socket?.send({ t: 'typing', chatId })
}

export function sendRead(chatId: string, messageId: string): void {
  socket?.send({ t: 'read', chatId, messageId })
}
